import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Message } from "primereact/message";
import { useFormik } from "formik";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";

import { InputSwitch } from "primereact/inputswitch";

import { InputFileCustom } from "../../components/InputFileCustom";
import { AppDialog, FormActions, FormField, FormRow, FormSection, FormToggle } from "../../components/ui";
import { BannerActiveLimitDialog } from "./BannerActiveLimitDialog";
import { BannerCharCounter } from "./BannerCharCounter";
import { BannerLandingPreview } from "./BannerLandingPreview";
import { BannerRichTextEditor } from "./BannerRichTextEditor";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useCreateBannerMutation, useUpdateBannerMutation } from "../../store/services/banners";
import { getAdminFacingApiErrorMessage, getApiErrorCode } from "../../utils/functions/api-errors";
import { parseDate } from "../../utils/functions/date";
import { CALENDAR_LOCALE } from "../../utils/constants/calendar-constants";
import {
  BANNER_ACTION_BUTTON_TYPE_OPTIONS,
  BANNER_APPEARANCE_COLORS,
  BANNER_CTA_TYPE_OPTIONS,
  BANNER_IMAGE_CROP_HEIGHT,
  BANNER_IMAGE_CROP_WIDTH,
  BANNER_IMAGE_MIN_HEIGHT,
  BANNER_IMAGE_MIN_WIDTH,
  BANNER_INTERNAL_ROUTE_OPTIONS,
  BANNER_LANDING_BODY_MAX_LENGTH,
  BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH,
  BANNER_LANDING_CALLOUT_MAX_LENGTH,
  BANNER_LANDING_TITLE_MAX_LENGTH,
  BANNER_SHORT_TEXT_MAX_LENGTH,
  BANNER_IMAGE_MAX_SIZE_KB,
  BANNER_STATUS_OPTIONS,
  Banner,
  BannerActionButtonType,
  BannerAppearanceColor,
  BannerCtaType,
  BannerLandingPage,
  BannerStatus,
  CreateBannerPayload,
  UpdateBannerPayload,
} from "../../models/banners";
import {
  SCHEDULE_AT_CAPACITY_WARNING,
  bannerCtaHasLoadedData,
  bannerFormValidationSchema,
  mapApiErrorToFormFields,
  showsScheduleAtCapacityWarning,
} from "./bannerFormRules";
import "./banners.css";

export interface BannerFormValues {
  name: string;
  shortText: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  imageSizeKb: number;
  ctaType: BannerCtaType;
  ctaValue: string;
  status: BannerStatus;
  sortOrder: number | null;
  startDate: Date | null;
  endDate: Date | null;
  /**
   * BN-010 — Landing de "Pagina Interna", plana y no anidada.
   *
   * Formik soporta objetos anidados, pero el resto del formulario (el `errorFor`, el mapeo de
   * `error.details[].field` de la API) indexa por clave simple. Una sola forma para todos los
   * campos es menos superficie para equivocarse que dos.
   */
  landingHeroImageUrl: string;
  landingTitle: string;
  /** HTML saneado del editor. El limite de 1000 se mide sobre su texto visible. */
  landingBody: string;
  landingCallout: string;
  /** Compatibilidad con la API; la apariencia visible usa el theme del tenant. */
  landingColor: BannerAppearanceColor;
  landingButtonEnabled: boolean;
  landingButtonText: string;
  landingButtonType: BannerActionButtonType;
  landingButtonValue: string;
}

export const toFormValues = (banner?: Banner): BannerFormValues => ({
  name: banner?.name ?? "",
  shortText: banner?.shortText ?? "",
  imageUrl: banner?.imageUrl ?? "",
  imageWidth: banner?.imageWidth ?? BANNER_IMAGE_MIN_WIDTH,
  imageHeight: banner?.imageHeight ?? BANNER_IMAGE_MIN_HEIGHT,
  imageSizeKb: banner?.imageSizeKb ?? 0,
  ctaType: banner?.ctaType ?? "external_url",
  ctaValue: banner?.ctaValue ?? "",
  status: banner?.status ?? "inactive",
  sortOrder: banner?.sortOrder ?? null,
  startDate: banner?.startDate ? parseDate(banner.startDate) : null,
  endDate: banner?.endDate ? parseDate(banner.endDate) : null,
  landingHeroImageUrl: banner?.landingPage?.heroImageUrl ?? "",
  landingTitle: banner?.landingPage?.title ?? "",
  landingBody: banner?.landingPage?.body ?? "",
  landingCallout: banner?.landingPage?.importantMessage ?? "",
  landingColor: banner?.landingPage?.appearanceColor ?? BANNER_APPEARANCE_COLORS[0],
  landingButtonEnabled: banner?.landingPage?.actionButtonEnabled ?? false,
  landingButtonText: banner?.landingPage?.actionButtonText ?? "",
  landingButtonType: banner?.landingPage?.actionButtonType ?? "external_url",
  landingButtonValue: banner?.landingPage?.actionButtonValue ?? "",
});

/**
 * La landing solo existe con `ctaType === "internal_page"`; con cualquier otro CTA se manda
 * `null` y el backend borra lo que hubiera.
 *
 * Criterio de aceptacion "Toggle Off no permite cargar texto/destino": apagado el switch los tres
 * campos del boton salen en `null` pase lo que pase con lo que quedo escrito en el estado.
 */
const toLandingPayload = (values: BannerFormValues): BannerLandingPage | null => {
  if (values.ctaType !== "internal_page") return null;
  const enabled = values.landingButtonEnabled;
  return {
    heroImageUrl: values.landingHeroImageUrl || null,
    title: values.landingTitle.trim(),
    body: values.landingBody.trim() === "" ? null : values.landingBody,
    importantMessage: values.landingCallout.trim() === "" ? null : values.landingCallout.trim(),
    appearanceColor: values.landingColor,
    actionButtonEnabled: enabled,
    actionButtonText: enabled ? values.landingButtonText.trim() || null : null,
    actionButtonType: enabled ? values.landingButtonType : null,
    actionButtonValue: enabled ? values.landingButtonValue.trim() || null : null,
  };
};

const LANDING_PAGE_KEYS: (keyof BannerLandingPage)[] = [
  "heroImageUrl",
  "title",
  "body",
  "importantMessage",
  "appearanceColor",
  "actionButtonEnabled",
  "actionButtonText",
  "actionButtonType",
  "actionButtonValue",
];

const sameLandingPage = (
  left: BannerLandingPage | null,
  right: BannerLandingPage | null
): boolean =>
  left === null || right === null
    ? left === right
    : LANDING_PAGE_KEYS.every((key) => left[key] === right[key]);

export const toCreatePayload = (values: BannerFormValues): CreateBannerPayload => ({
  name: values.name.trim(),
  shortText: values.shortText.trim() === "" ? null : values.shortText.trim(),
  imageUrl: values.imageUrl,
  imageWidth: values.imageWidth,
  imageHeight: values.imageHeight,
  imageSizeKb: values.imageSizeKb,
  ctaType: values.ctaType,
  ctaValue: values.ctaType === "internal_page" ? null : values.ctaValue.trim() || null,
  status: values.status,
  startDate: values.startDate?.toISOString() ?? null,
  endDate: values.endDate?.toISOString() ?? null,
  ...(values.sortOrder === null ? {} : { sortOrder: values.sortOrder }),
  landingPage: toLandingPayload(values),
});

export const toUpdatePayload = (banner: Banner, values: BannerFormValues): UpdateBannerPayload => {
  const original = toCreatePayload(toFormValues(banner));
  const next = toCreatePayload(values);
  const changes: UpdateBannerPayload = Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) => !["landingPage", "startDate", "endDate"].includes(key) && value !== original[key as keyof CreateBannerPayload]
    )
  );
  // `landingPage` es un objeto: el `!==` de arriba lo daria cambiado siempre, asi que va aparte.
  if (!sameLandingPage(original.landingPage, next.landingPage)) changes.landingPage = next.landingPage;
  if (values.imageUrl !== banner.imageUrl) {
    changes.imageWidth = values.imageWidth;
    changes.imageHeight = values.imageHeight;
    changes.imageSizeKb = values.imageSizeKb;
  }
  const startDate = values.startDate?.toISOString() ?? null;
  const endDate = values.endDate?.toISOString() ?? null;
  if (startDate !== (banner.startDate ? new Date(banner.startDate).toISOString() : null)) changes.startDate = startDate;
  if (endDate !== (banner.endDate ? new Date(banner.endDate).toISOString() : null)) changes.endDate = endDate;
  return changes;
};

interface BannerFormProps {
  /** Activos del tenant sin contar el que se esta editando. Los programados no ocupan cupo. */
  activeBannerCount?: number;
  tenantId: string;
  banner?: Banner;
  disabled?: boolean;
  onClose: () => void;
}

export const BannerForm = ({ tenantId, banner, disabled = false, activeBannerCount = 0, onClose }: BannerFormProps) => {
  const initialBanner = useRef(banner).current;
  const statusOptions = initialBanner
    ? BANNER_STATUS_OPTIONS
    : BANNER_STATUS_OPTIONS.filter((option) => option.value !== "finished");
  const requestPending = useRef(false);
  const [submitError, setSubmitError] = useState<string>();
  const [showActiveLimit, setShowActiveLimit] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const imageBusyRef = useRef(false);
  const [imageError, setImageError] = useState<string>();
  // La landing tiene su propio cargador (imagen hero), asi que el "esperá a que termine de
  // procesarse" tiene que mirar los dos: uno solo dejaria guardar con el otro a medio subir.
  const [heroBusy, setHeroBusy] = useState(false);
  const heroBusyRef = useRef(false);
  const [heroError, setHeroError] = useState<string>();
  const [createBanner, { isLoading: isCreating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();
  const [pendingCtaType, setPendingCtaType] = useState<BannerCtaType | null>(null);
  const [ctaTypeMenuKey, setCtaTypeMenuKey] = useState(0);
  const formik = useFormik<BannerFormValues>({
    initialValues: toFormValues(initialBanner),
    validationSchema: bannerFormValidationSchema,
    onSubmit: async (values, helpers) => {
      if (requestPending.current || disabled) return;
      if (imageBusyRef.current || heroBusyRef.current || imageError || heroError) return;
      setSubmitError(undefined);
      const changes = initialBanner ? toUpdatePayload(initialBanner, values) : undefined;
      if (changes && Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      requestPending.current = true;
      try {
        if (initialBanner && changes) {
          await updateBanner({ tenantId, id: initialBanner.id, body: changes }).unwrap();
        } else {
          await createBanner({ tenantId, body: toCreatePayload(values) }).unwrap();
        }
        toast.success(initialBanner ? "Banner actualizado" : "Banner creado");
        onClose();
      } catch (error) {
        if (getApiErrorCode(error) === "MAX_ACTIVE_BANNERS_REACHED") {
          setShowActiveLimit(true);
          return;
        }
        const fieldErrors = mapApiErrorToFormFields(error);
        helpers.setErrors(fieldErrors);
        setSubmitError(Object.values(fieldErrors).join(" ") || getAdminFacingApiErrorMessage(error, "No se pudo guardar el banner"));
      } finally {
        requestPending.current = false;
      }
    },
  });

  const isInternalPage = formik.values.ctaType === "internal_page";

  /**
   * `InputFileCustom` guardea sus callbacks con un `mountedRef`: desmontado, no vuelve a avisar
   * nunca. Como el cargador del hero solo existe dentro de la seccion de Pagina Interna, un error
   * o una subida en vuelo al momento de cambiar de CTA dejaba `heroError`/`heroBusy` prendidos
   * para siempre: el boton de guardar quedaba deshabilitado por un campo que ya no esta en
   * pantalla, y con `heroBusy` ni "Cancelar" respondia. Se limpian al desmontarse la seccion.
   */
  useEffect(() => {
    if (isInternalPage) return;
    heroBusyRef.current = false;
    setHeroBusy(false);
    setHeroError(undefined);
  }, [isInternalPage]);

  const saving = formik.isSubmitting || isCreating || isUpdating;
  const uploadBusy = imageBusy || heroBusy;
  const uploadError = imageError ?? heroError;
  useUnsavedChangesGuard(formik.dirty || saving || uploadBusy);
  const errorFor = (field: keyof BannerFormValues): string | undefined =>
    (formik.touched[field] || formik.submitCount > 0 || field === "shortText" || field === "imageSizeKb") && formik.errors[field]
      ? String(formik.errors[field])
      : undefined;

  /** Cambiar de CTA descarta el destino Y la landing: son configuraciones mutuamente excluyentes. */
  const applyCtaType = (next: BannerCtaType) =>
    formik.setValues((values) => ({
      ...values,
      ctaType: next,
      ctaValue: "",
      landingHeroImageUrl: "",
      landingTitle: "",
      landingBody: "",
      landingCallout: "",
      landingColor: BANNER_APPEARANCE_COLORS[0],
      landingButtonEnabled: false,
      landingButtonText: "",
      landingButtonType: "external_url" as BannerActionButtonType,
      landingButtonValue: "",
    }));

  const landingDraft = {
    heroImageUrl: formik.values.landingHeroImageUrl,
    title: formik.values.landingTitle,
    body: formik.values.landingBody,
    callout: formik.values.landingCallout,
    color: formik.values.landingColor,
    buttonEnabled: formik.values.landingButtonEnabled,
    buttonText: formik.values.landingButtonText,
    buttonValue: formik.values.landingButtonValue,
  };

  const requestCtaTypeChange = (next: BannerCtaType) => {
    if (next === formik.values.ctaType) return;
    if (!bannerCtaHasLoadedData(formik.values.ctaType, formik.values.ctaValue, landingDraft)) {
      applyCtaType(next);
      return;
    }
    setPendingCtaType(next);
  };

  const setLandingButtonEnabled = (enabled: boolean) =>
    formik.setValues((values) => ({
      ...values,
      landingButtonEnabled: enabled,
      // Apagado no se puede "cargar texto/destino": lo que hubiera escrito se descarta ahora,
      // no solo al armar el payload.
      ...(enabled
        ? {}
        : { landingButtonText: "", landingButtonValue: "", landingButtonType: "external_url" as BannerActionButtonType }),
    }));

  return (
    <>
    <form
      className="banner-form"
      aria-label="Datos del banner"
      onSubmit={formik.handleSubmit}
      noValidate
    >
      {submitError && <div className="mb-3"><Message severity="error" text={submitError} /></div>}
      <fieldset className="banner-form-fields">
        {/* Filas llenas de punta a punta, como el .row/.field del mockup
            (SYSTEM_DESIGN §5): el ancho de cada caja sale de cuantos campos
            comparten la fila, no de un tope por campo. */}
        <FormRow>
          <FormField label="Nombre" htmlFor="banner-name" required error={errorFor("name")}>
            <InputText
              id="banner-name"
              value={formik.values.name}
              disabled={disabled || saving}
              onChange={(event) => formik.setFieldValue("name", event.target.value)}
              onBlur={() => formik.setFieldTouched("name", true)}
            />
          </FormField>

          <FormField label="Estado" htmlFor="banner-status" error={errorFor("status")}>
            <Dropdown
              inputId="banner-status"
              disabled={disabled || saving}
              value={formik.values.status}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => formik.setFieldValue("status", event.value)}
            />
          </FormField>

        </FormRow>

        {showsScheduleAtCapacityWarning(formik.values.status, activeBannerCount) && (
          <Message
            severity="warn"
            className="w-full mb-3"
            text={SCHEDULE_AT_CAPACITY_WARNING}
          />
        )}

        <FormRow>
          {(["startDate", "endDate"] as const).map((field) => (
            <FormField key={field} label={field === "startDate" ? "Fecha de inicio" : "Fecha de fin"}
              htmlFor={`banner-${field}`}
              required={formik.values.status === "scheduled"}
              hint={
                field === "startDate"
                  ? "Opcional. Si el estado es Programado, el banner se activa solo en esta fecha."
                  : "Opcional. El banner se da de baja solo al llegar esta fecha."
              }
              error={errorFor(field)}>
                <Calendar inputId={`banner-${field}`} name={field} value={formik.values[field]}
                  onChange={(event) => void formik.setFieldValue(field, event.value ?? null)}
                  onBlur={() => void formik.setFieldTouched(field, true)}
                  dateFormat="dd/mm/yy" showTime hourFormat="24" showIcon showButtonBar locale={CALENDAR_LOCALE}
                disabled={disabled || saving} />
            </FormField>
          ))}
        </FormRow>

        <FormRow>
          <FormField
            label="Texto corto"
            htmlFor="banner-short-text"
            hint={`Opcional. Máximo ${BANNER_SHORT_TEXT_MAX_LENGTH} caracteres.`}
            error={errorFor("shortText")}
          >
            <InputTextarea
              id="banner-short-text"
              value={formik.values.shortText}
              rows={2}
              autoResize
              disabled={disabled || saving}
              onChange={(event) => formik.setFieldValue("shortText", event.target.value)}
              onBlur={() => formik.setFieldTouched("shortText", true)}
            />
            <small className="text-right text-600" aria-live="polite" aria-label="Caracteres del texto corto">
              {formik.values.shortText.length}/{BANNER_SHORT_TEXT_MAX_LENGTH}
            </small>
          </FormField>
        </FormRow>

        {/* Cargador y vista previa comparten fila, como la portada y las
            imagenes adicionales del mockup: el archivo de un lado y como se ve
            del otro, en vez de la misma foto dos veces una debajo de la otra. */}
        <div className={`banner-media-row${formik.values.imageUrl ? " banner-media-row--with-preview" : ""}`}>
          <FormField
            label="Imagen"
            className="banner-image-field"
            required
            hint={`Mínimo ${BANNER_IMAGE_MIN_WIDTH}x${BANNER_IMAGE_MIN_HEIGHT}px. Se sube sin recortar: la app la muestra como una franja y recorta el resto.`}
            error={errorFor("imageUrl") || errorFor("imageSizeKb")}
          >
            <InputFileCustom
              disabled={disabled || saving}
              initialImage={formik.values.imageUrl}
              minWidth={BANNER_IMAGE_MIN_WIDTH}
              minHeight={BANNER_IMAGE_MIN_HEIGHT}
              uploadFolder="banners"
              maxSizeKb={BANNER_IMAGE_MAX_SIZE_KB}
              imageClassName="banner-upload-preview"
              onError={setImageError}
              onBusyChange={(busy) => { imageBusyRef.current = busy; setImageBusy(busy); }}
              handleInputFile={(url) => formik.setFieldValue("imageUrl", url)}
              handleInputFileClear={() => {
                void formik.setValues((values) => ({ ...values, imageUrl: "", imageSizeKb: 0 }));
              }}
              onUploaded={(meta) => {
                void formik.setValues((values) => ({
                  ...values,
                  imageUrl: meta.url,
                  imageSizeKb: meta.sizeKb,
                  imageWidth: meta.width,
                  imageHeight: meta.height,
                }));
              }}
            />
            {!!formik.values.imageSizeKb && <small className="text-600">{formik.values.imageSizeKb} / {BANNER_IMAGE_MAX_SIZE_KB} KB</small>}
          </FormField>

          {formik.values.imageUrl && (
            <section className="banner-preview" aria-label="Vista previa del banner">
              <h2>Vista previa</h2>
              <img src={formik.values.imageUrl} alt={formik.values.name || "Banner"} />
              {formik.values.shortText && <p>{formik.values.shortText}</p>}
              <small className="text-600">{BANNER_CTA_TYPE_OPTIONS.find((option) => option.value === formik.values.ctaType)?.label}</small>
              {formik.values.ctaValue && <p className="text-sm">{formik.values.ctaValue}</p>}
            </section>
          )}
        </div>

        <FormRow>
          <FormField label="Acción al tocar" htmlFor="banner-cta-type" required error={errorFor("ctaType")}>
            <Dropdown
              inputId="banner-cta-type"
              key={ctaTypeMenuKey}
              disabled={disabled || saving}
              value={formik.values.ctaType}
              options={BANNER_CTA_TYPE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => requestCtaTypeChange(event.value)}
            />
          </FormField>

          {!isInternalPage && (
            <FormField
              htmlFor="banner-cta-value"
              label={formik.values.ctaType === "internal_route" ? "Sección" : "Destino"}
              required
              hint={
                formik.values.ctaType === "internal_route"
                  ? "Las versiones de la app ya publicadas todavía no navegan a estas secciones: el banner se ve pero el toque no hace nada hasta que salga la actualización."
                  : undefined
              }
              error={errorFor("ctaValue")}
            >
              {formik.values.ctaType === "internal_route" ? (
                <Dropdown
                  inputId="banner-cta-value"
                  disabled={disabled || saving}
                  value={formik.values.ctaValue}
                  options={BANNER_INTERNAL_ROUTE_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Elegí una sección"
                  onChange={(event) => formik.setFieldValue("ctaValue", event.value)}
                />
              ) : (
                <InputText
                  id="banner-cta-value"
                  value={formik.values.ctaValue}
                  placeholder="https://"
                  disabled={disabled || saving}
                  onChange={(event) => formik.setFieldValue("ctaValue", event.target.value)}
                  onBlur={() => formik.setFieldTouched("ctaValue", true)}
                />
              )}
            </FormField>
          )}
        </FormRow>

        {/* Spec §3.1: al elegir "Página Interna" la configuración se expande EN el mismo
            formulario (acordeón que empuja el contenido), nunca en un modal. La sección solo se
            monta con ese CTA, así que con cualquier otro las reglas de Yup no aplican y los
            campos no pueden bloquear el submit. */}
        {isInternalPage && (
          <FormSection
            title="Configuración de la Página Interna"
            icon="pi-file-edit"
            defaultOpen
            className="banner-landing-section"
          >
            <div className="banner-landing-row">
              <div className="banner-landing-fields">
                <FormRow>
                  <FormField
                    label="Imagen principal"
                    className="banner-image-field"
                    required
                    hint={`Se recorta a ${BANNER_IMAGE_CROP_WIDTH}x${BANNER_IMAGE_CROP_HEIGHT} (16:9). JPG o PNG, máximo ${BANNER_IMAGE_MAX_SIZE_KB} KB.`}
                    error={errorFor("landingHeroImageUrl") || heroError}
                  >
                    <InputFileCustom
                      disabled={disabled || saving}
                      initialImage={formik.values.landingHeroImageUrl}
                      cropWidth={BANNER_IMAGE_CROP_WIDTH}
                      cropHeight={BANNER_IMAGE_CROP_HEIGHT}
                      uploadFolder="banners"
                      maxSizeKb={BANNER_IMAGE_MAX_SIZE_KB}
                      imageClassName="banner-upload-preview"
                      onError={setHeroError}
                      onBusyChange={(busy) => {
                        heroBusyRef.current = busy;
                        setHeroBusy(busy);
                      }}
                      handleInputFile={(url) => formik.setFieldValue("landingHeroImageUrl", url)}
                      handleInputFileClear={() => formik.setFieldValue("landingHeroImageUrl", "")}
                      onUploaded={(meta) => formik.setFieldValue("landingHeroImageUrl", meta.url)}
                    />
                  </FormField>
                </FormRow>

                <FormRow>
                  <FormField
                    label="Título"
                    htmlFor="banner-landing-title"
                    required
                    error={errorFor("landingTitle")}
                  >
                    {/* `maxLength` es el bloqueo duro que pide la spec §3.3: el input deja de
                        aceptar caracteres en el tope, no muestra un error después. */}
                    <InputText
                      id="banner-landing-title"
                      value={formik.values.landingTitle}
                      maxLength={BANNER_LANDING_TITLE_MAX_LENGTH}
                      disabled={disabled || saving}
                      onChange={(event) => formik.setFieldValue("landingTitle", event.target.value)}
                      onBlur={() => formik.setFieldTouched("landingTitle", true)}
                    />
                    <BannerCharCounter
                      value={formik.values.landingTitle.length}
                      max={BANNER_LANDING_TITLE_MAX_LENGTH}
                      label="título de la página"
                    />
                  </FormField>
                </FormRow>

                <FormRow>
                  <FormField
                    label="Mensaje destacado"
                    htmlFor="banner-landing-callout"
                    hint="Opcional. Se muestra justo debajo del título, como resumen. Sin texto, el bloque no aparece."
                    hintPosition="above"
                    error={errorFor("landingCallout")}
                  >
                    <InputTextarea
                      id="banner-landing-callout"
                      value={formik.values.landingCallout}
                      maxLength={BANNER_LANDING_CALLOUT_MAX_LENGTH}
                      rows={2}
                      autoResize
                      disabled={disabled || saving}
                      onChange={(event) => formik.setFieldValue("landingCallout", event.target.value)}
                      onBlur={() => formik.setFieldTouched("landingCallout", true)}
                    />
                    <BannerCharCounter
                      value={formik.values.landingCallout.length}
                      max={BANNER_LANDING_CALLOUT_MAX_LENGTH}
                      label="mensaje destacado"
                    />
                  </FormField>
                </FormRow>

                <FormRow>
                  <FormField
                    label="Cuerpo"
                    htmlFor="banner-landing-body"
                    required
                    hint={`Negrita, cursiva y links. El editor deja de aceptar texto al llegar a ${BANNER_LANDING_BODY_MAX_LENGTH} caracteres.`}
                    error={errorFor("landingBody")}
                  >
                    <BannerRichTextEditor
                      id="banner-landing-body"
                      value={formik.values.landingBody}
                      maxLength={BANNER_LANDING_BODY_MAX_LENGTH}
                      disabled={disabled || saving}
                      invalid={!!errorFor("landingBody")}
                      placeholder="Escribí el contenido de la página…"
                      onChange={(html) => formik.setFieldValue("landingBody", html)}
                      onBlur={() => formik.setFieldTouched("landingBody", true)}
                    />
                  </FormField>
                </FormRow>

                <FormRow>
                  <FormToggle
                    label="Mostrar botón de acción"
                    htmlFor="banner-landing-button-enabled"
                    hint="Apagado, la página se ve sin botón y el texto ocupa todo el alto. El toque en el banner del carrusel ya lleva a esta página."
                  >
                    <InputSwitch
                      inputId="banner-landing-button-enabled"
                      checked={formik.values.landingButtonEnabled}
                      disabled={disabled || saving}
                      onChange={(event) => setLandingButtonEnabled(event.value === true)}
                    />
                  </FormToggle>
                </FormRow>

                {/* Criterio de aceptación: con el toggle en Off no hay dónde cargar texto ni
                    destino. No se deshabilitan, no existen. */}
                {formik.values.landingButtonEnabled && (
                  <FormRow>
                    <FormField
                      label="Texto del botón"
                      htmlFor="banner-landing-button-text"
                      required
                      error={errorFor("landingButtonText")}
                    >
                      <InputText
                        id="banner-landing-button-text"
                        value={formik.values.landingButtonText}
                        maxLength={BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH}
                        disabled={disabled || saving}
                        onChange={(event) =>
                          formik.setFieldValue("landingButtonText", event.target.value)
                        }
                        onBlur={() => formik.setFieldTouched("landingButtonText", true)}
                      />
                      <BannerCharCounter
                        value={formik.values.landingButtonText.length}
                        max={BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH}
                        label="texto del botón"
                      />
                    </FormField>

                    <FormField
                      label="Tipo de destino"
                      htmlFor="banner-landing-button-type"
                      required
                      error={errorFor("landingButtonType")}
                    >
                      <Dropdown
                        inputId="banner-landing-button-type"
                        value={formik.values.landingButtonType}
                        options={BANNER_ACTION_BUTTON_TYPE_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        disabled={disabled || saving}
                        onChange={(event) =>
                          formik.setValues((values) => ({
                            ...values,
                            landingButtonType: event.value,
                            landingButtonValue: "",
                          }))
                        }
                      />
                    </FormField>

                    <FormField
                      label={formik.values.landingButtonType === "internal_route" ? "Sección" : "Destino"}
                      htmlFor="banner-landing-button-value"
                      required
                      error={errorFor("landingButtonValue")}
                    >
                      {formik.values.landingButtonType === "internal_route" ? (
                        <Dropdown
                          inputId="banner-landing-button-value"
                          value={formik.values.landingButtonValue}
                          options={BANNER_INTERNAL_ROUTE_OPTIONS}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Elegí una sección"
                          disabled={disabled || saving}
                          onChange={(event) =>
                            formik.setFieldValue("landingButtonValue", event.value)
                          }
                        />
                      ) : (
                        <InputText
                          id="banner-landing-button-value"
                          value={formik.values.landingButtonValue}
                          placeholder="https://"
                          disabled={disabled || saving}
                          onChange={(event) =>
                            formik.setFieldValue("landingButtonValue", event.target.value)
                          }
                          onBlur={() => formik.setFieldTouched("landingButtonValue", true)}
                        />
                      )}
                    </FormField>
                  </FormRow>
                )}
              </div>

              <BannerLandingPreview
                tenantId={tenantId}
                heroImageUrl={formik.values.landingHeroImageUrl}
                title={formik.values.landingTitle}
                body={formik.values.landingBody}
                callout={formik.values.landingCallout}
                buttonEnabled={formik.values.landingButtonEnabled}
                buttonText={formik.values.landingButtonText}
              />
            </div>
          </FormSection>
        )}
      </fieldset>
      <FormActions
        primaryLabel={initialBanner ? "Guardar cambios" : "Crear banner"}
        loading={saving}
        disabled={disabled || saving || uploadBusy || !!uploadError}
        disabledReason={uploadBusy ? "Esperá a que termine de procesarse la imagen" : uploadError}
        onCancel={() => {
          if (!requestPending.current && !imageBusyRef.current && !heroBusyRef.current) onClose();
        }}
      />
    </form>
    <BannerActiveLimitDialog visible={showActiveLimit} onHide={() => setShowActiveLimit(false)} />
    <AppDialog
      visible={pendingCtaType !== null}
      onHide={() => {
        setPendingCtaType(null);
        setCtaTypeMenuKey((key) => key + 1);
      }}
      header="¿Querés continuar?"
      variant="form"
      primaryLabel="Continuar"
      onPrimary={() => {
        const next = pendingCtaType;
        if (!next) {
          setPendingCtaType(null);
          return;
        }
        void applyCtaType(next).then(() => setPendingCtaType(null));
      }}
    >
      <p>Vas a perder la configuración actual</p>
    </AppDialog>
    </>
  );
};
